import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormField, Forminator } from '@nuso/forms/data-access';
import { Observable } from 'rxjs';
import { BaseFormInput } from '../base-form-input';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';

interface FieldOrSpacerColumn {
  subfield: FormField | undefined;
  visible: boolean;
}
@Component({
    selector: 'nuso-row-input',
    templateUrl: './row-input.component.html',
    styleUrls: ['./row-input.component.css'],
    standalone: false
})
export class RowInputComponent extends BaseFormInput implements OnInit {
  @Input() override field: FormField;

  columnList$: Observable<FieldOrSpacerColumn[]>;
  minVisibleColumns: number;

  override ngOnInit() {
    super.ngOnInit();
    this.minVisibleColumns = this.field.attrs?.minVisibleColumns || this.field.fieldList?.length || 0;

    const form = this.field.form;
    if (!form) {
      throw new Error('RowInputComponent requires field.form to be set by Forminator.createForm');
    }

    this.columnList$ = form.group.valueChanges
      .pipe(
        startWith(true), // value doesn't matter, just need to call first time
        map(() => this.fieldListWithVisibility()),
        distinctUntilChanged((prev, curr) => visibleFieldsString(prev) === visibleFieldsString(curr)),
        map((fieldList) => this.fieldListWithSpacers(fieldList)),
      )
  }

  fieldListWithVisibility(): FieldOrSpacerColumn[] {
    const formFields: FieldOrSpacerColumn[] = (this.field.fieldList || []).map((subfield) => ({
      subfield,
      visible: Forminator.dependenciesMet(subfield, subfield.showIfDependencies || [])
    }));

    return formFields;
  }

  fieldListWithSpacers(formFields: FieldOrSpacerColumn[]) {
    const visibleCount = formFields.reduce((total: number, f) => (f.visible ? total + 1 : total), 0);
    const spacerCount = this.minVisibleColumns - visibleCount;
    const spacers = Array.from({ length: spacerCount < 0 ? 0 : spacerCount }).map(() => ({
      subfield: undefined,
      visible: true
    }));

    return formFields.concat(spacers);
  }

  override hasDanger(): boolean {
    // Only surface a row-level hint when the FormGroup itself has direct
    // errors set by a group-level validator (e.g. `allOrNothing`). Per-field
    // errors are already rendered inside each child input; falling through
    // here when only child controls are invalid produced a noisy
    // "<row name> ." hint with no useful info, because FormGroup.errors is
    // null while FormGroup.valid is false in that case.
    if (!this.field.control.errors) return false;

    const fields = this.field.fields || {};
    const allSubFieldsTouched = Object.keys(fields).every(key => fields[key].control.touched);
    const isInvalid = !this.field.control.valid;

    return !this.isDisabled() && (
      (allSubFieldsTouched && isInvalid)
      || (!!this.field.form?.submitted && isInvalid)
    );
  }


}

// Compare strings of the visible fields names for purposes of the distincUntilChangesd
function visibleFieldsString(fields: FieldOrSpacerColumn[]) {
  return fields
    .filter((f): f is FieldOrSpacerColumn & { subfield: FormField } => f.visible && !!f.subfield)
    .map(f => f.subfield.name)
    .join(',');
}
